import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Group,
  Loader,
  NumberInput,
  Pagination,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconBattery2,
  IconCheck,
  IconCircleCheck,
  IconCreditCard,
  IconDroplet,
  IconHistory,
  IconPhone,
  IconQrcode,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import PublicLayout from "../../components/layouts/PublicLayout";
import {
  fetchMeterBillingPublicAsync,
  initiateSTKPushAsync,
} from "../../actions/billing.actions";
import { fetchMeterDeviceInfoAsync, fetchMeterPublicInfoAsync } from "../../actions/meters.actions";
import { BillingResponseType, FengboDeviceInfoItem, MeterDeviceInfoResponse, TransactionType } from "../../types";
import { formatDate } from "../../utils/date";
import { formatNumberWithCommas } from "../../utils/numbers";

const PAGE_SIZE = 10;
const MIN_AMOUNT = 20;

function normalizePhone(input: string): string {
  const digits = input.replace(/\D/g, "");
  if (digits.startsWith("0") && digits.length === 10) return "254" + digits.slice(1);
  return digits;
}

function isValidPhone(input: string): boolean {
  return /^254\d{9}$/.test(normalizePhone(input));
}

function humanizeError(raw: string): string {
  if (!raw) return "Payment processing failed. Contact support.";
  if (/below minimum/i.test(raw)) {
    const match = raw.match(/minimum\s+([\d.]+)/i);
    const min = match ? `KES ${match[1]}` : "KES 20";
    return `Payment too low. Minimum recharge is ${min}.`;
  }
  if (/unsuccessful water purchase records/i.test(raw))
    return "Recharge failed. Previous amount not loaded. Contact support.";
  if (/NoClassDefFoundError|Handler dispatch failed/i.test(raw))
    return "Recharge failed. A system error occurred. Contact support.";
  if (/522|Connection timed out|cloudflare/i.test(raw))
    return "Recharge failed. Service was unreachable at the time. Contact support.";
  if (/not found in smartmeters/i.test(raw))
    return "Meter not registered in the system. Contact support.";
  return "Payment processing failed. Contact support.";
}

export default function MeterLookupPage() {
  const [searchParams] = useSearchParams();
  const prefilled = searchParams.get("meter") ?? "";

  // Meter search
  const [meterInput, setMeterInput] = useState(prefilled);
  const [submittedMeter, setSubmittedMeter] = useState<string | null>(prefilled || null);
  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<string | null>("history");

  // House label from DB
  const [labelDetails, setLabelDetails] = useState<string | null>(null);

  // Device info (live meter data from provider API)
  const [deviceInfo, setDeviceInfo] = useState<FengboDeviceInfoItem | null>(null);
  const [deviceInfoLoading, setDeviceInfoLoading] = useState(false);
  const [deviceInfoError, setDeviceInfoError] = useState(false);

  // STK push
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState<number | string>("");
  const [stkLoading, setStkLoading] = useState(false);
  const [stkSent, setStkSent] = useState(false);
  const [stkError, setStkError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);

  useEffect(() => {
    if (prefilled) {
      fetchBilling(prefilled, 1);
    } else {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBilling = async (meterNo: string, pageNum: number) => {
    setLoading(true);
    setNotFound(false);
    setFetchError(false);

    try {
      const result = await fetchMeterBillingPublicAsync(meterNo.trim(), pageNum, PAGE_SIZE);
      const response = (result as any)?.data as BillingResponseType | undefined;

      if (response && Array.isArray(response.data)) {
        if (response.data.length === 0 && pageNum === 1) {
          setNotFound(true);
          setTransactions([]);
        } else {
          setTransactions(response.data);
          setPage(response.currentPage);
          setTotalPages(response.totalPages);
          setSubmittedMeter(meterNo.trim());
          if (pageNum === 1) {
            fetchDeviceInfo(meterNo.trim());
            fetchMeterPublicInfoAsync(meterNo.trim())
              .then((res: any) => setLabelDetails(res?.labelDetails ?? null))
              .catch(() => {});
          }
        }
      } else {
        setNotFound(true);
        setTransactions([]);
      }
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceInfo = async (meterNo: string) => {
    setDeviceInfoLoading(true);
    setDeviceInfoError(false);
    setDeviceInfo(null);
    try {
      const response = await fetchMeterDeviceInfoAsync(meterNo) as MeterDeviceInfoResponse;
      const item = response?.data?.[0] ?? null;
      setDeviceInfo(item);
    } catch {
      setDeviceInfoError(true);
    } finally {
      setDeviceInfoLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!meterInput.trim()) return;
    setPage(1);
    setTransactions([]);
    fetchBilling(meterInput, 1);
  };

  const handlePageChange = (p: number) => {
    if (submittedMeter) fetchBilling(submittedMeter, p);
  };

  const resetTopUp = () => {
    setPhone("");
    setAmount("");
    setStkSent(false);
    setStkError(null);
    setPhoneError(null);
    setAmountError(null);
  };

  const handleClear = () => {
    setMeterInput("");
    setSubmittedMeter(null);
    setTransactions([]);
    setNotFound(false);
    setFetchError(false);
    setDeviceInfo(null);
    setDeviceInfoError(false);
    setLabelDetails(null);
    resetTopUp();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleTopUp = async (e: React.FormEvent) => {
    e.preventDefault();

    let valid = true;

    if (!isValidPhone(phone)) {
      setPhoneError("Enter a valid Kenyan number (e.g. 0712 345 678)");
      valid = false;
    } else {
      setPhoneError(null);
    }

    const amt = Number(amount);
    if (!amount || amt < MIN_AMOUNT) {
      setAmountError(`Minimum amount is KES ${MIN_AMOUNT}`);
      valid = false;
    } else {
      setAmountError(null);
    }

    if (!valid || !submittedMeter) return;

    setStkLoading(true);
    setStkError(null);

    try {
      await initiateSTKPushAsync({
        meterNo: submittedMeter,
        phoneNumber: normalizePhone(phone),
        amount: amt,
      });
      setStkSent(true);
    } catch {
      setStkError("Could not initiate payment. Please try again.");
    } finally {
      setStkLoading(false);
    }
  };

  const meterFound = transactions.length > 0 && submittedMeter;

  return (
    <PublicLayout>
      <Stack gap="lg" maw={560} mx="auto">

        {/* Hero + full search — only before a meter is loaded */}
        {!meterFound && (
          <>
            <Box
              style={{
                background: "linear-gradient(135deg, #03045e 0%, #0077b6 100%)",
                borderRadius: 16,
                padding: "28px 24px",
                textAlign: "center",
              }}
            >
              <ThemeIcon
                size={56}
                radius="xl"
                style={{ background: "rgba(255,255,255,0.15)", margin: "0 auto 12px" }}
              >
                <IconQrcode size={28} color="white" />
              </ThemeIcon>
              <Title order={2} c="white" mb={4}>
                Meter Self-Service
              </Title>
              <Text size="sm" c="rgba(255,255,255,0.75)">
                View your payment history or top up your meter via M-Pesa
              </Text>
            </Box>

            <Card shadow="sm" radius="lg" withBorder p="xl">
              <form onSubmit={handleSearch}>
                <Stack gap="md">
                  <TextInput
                    ref={inputRef}
                    label="Meter Number"
                    description="Found on your meter or payment receipt"
                    placeholder="e.g. 70666000096297"
                    value={meterInput}
                    onChange={(e) => setMeterInput(e.currentTarget.value)}
                    size="lg"
                    radius="md"
                    leftSection={<IconDroplet size={18} color="#0077b6" />}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    styles={{
                      input: { fontSize: 16, letterSpacing: 1 },
                      label: { fontWeight: 700, fontSize: 14 },
                    }}
                  />
                  <Button
                    type="submit"
                    size="lg"
                    radius="md"
                    fullWidth
                    leftSection={<IconSearch size={18} />}
                    loading={loading}
                    disabled={!meterInput.trim()}
                    style={{ background: "linear-gradient(135deg, #03045e 0%, #0077b6 100%)" }}
                  >
                    Find Meter
                  </Button>
                </Stack>
              </form>
            </Card>
          </>
        )}

        {/* Compact header — replaces hero + search once meter is loaded */}
        {meterFound && (
          <Card
            shadow="sm"
            radius="lg"
            withBorder
            px="lg"
            py="sm"
            style={{
              background: "linear-gradient(135deg, #03045e 0%, #0077b6 100%)",
            }}
          >
            <Group justify="space-between" align="center">
              <Group gap="sm">
                <ThemeIcon size={38} radius="md" style={{ background: "rgba(255,255,255,0.15)" }}>
                  <IconDroplet size={20} color="white" />
                </ThemeIcon>
                <Box>
                  <Text size="xs" c="rgba(255,255,255,0.65)" fw={600} tt="uppercase">
                    Meter
                  </Text>
                  <Text fw={800} ff="monospace" c="white" size="md">
                    {submittedMeter}
                  </Text>
                  {labelDetails && (
                    <Text size="xs" c="rgba(255,255,255,0.75)" mt={1}>
                      {labelDetails}
                    </Text>
                  )}
                </Box>
              </Group>
              <Button
                size="xs"
                radius="xl"
                variant="white"
                color="dark"
                leftSection={<IconSearch size={12} />}
                onClick={handleClear}
              >
                Change
              </Button>
            </Group>
          </Card>
        )}

        {/* Loading */}
        {loading && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Loader color="#0077b6" size="md" type="dots" />
              <Text size="sm" c="dimmed">Looking up meter...</Text>
            </Stack>
          </Center>
        )}

        {/* Not found */}
        {!loading && notFound && (
          <Alert icon={<IconAlertCircle size={20} />} color="orange" radius="lg" title="No records found" variant="light">
            No records found for meter <strong>{meterInput.trim()}</strong>.
            Please check the number and try again.
          </Alert>
        )}

        {/* Fetch error */}
        {!loading && fetchError && (
          <Alert icon={<IconAlertCircle size={20} />} color="red" radius="lg" title="Something went wrong" variant="light">
            Could not retrieve meter records. Please try again later.
          </Alert>
        )}

        {/* Meter found */}
        {!loading && meterFound && (
          <Stack gap="md">

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
              radius="md"
              styles={{ tab: { fontWeight: 600 }, list: { borderColor: "#e2e8f0" } }}
            >
              <Tabs.List grow>
                <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
                  Payment History
                </Tabs.Tab>
                <Tabs.Tab value="topup" leftSection={<IconCreditCard size={16} />}>
                  Top Up
                </Tabs.Tab>
              </Tabs.List>

              {/* --- History tab --- */}
              <Tabs.Panel value="history" pt="md">
                <Stack gap="md">
                  <DeviceInfoCard
                    info={deviceInfo}
                    loading={deviceInfoLoading}
                    error={deviceInfoError}
                  />

                  {transactions.map((tx) => (
                    <PaymentCard key={tx.id} tx={tx} />
                  ))}

                  {totalPages > 1 && (
                    <Group justify="center" pt="xs">
                      <Pagination
                        total={totalPages}
                        value={page}
                        onChange={handlePageChange}
                        size="md"
                        radius="md"
                        color="#03045e"
                      />
                    </Group>
                  )}
                </Stack>
              </Tabs.Panel>

              {/* --- Top Up tab --- */}
              <Tabs.Panel value="topup" pt="md">
                {stkSent ? (
                  <StkSentCard phone={phone} onReset={resetTopUp} />
                ) : (
                  <Card shadow="sm" radius="lg" withBorder p="xl">
                    <Stack gap="xs" mb="lg">
                      <Title order={4} c="#03045e">Top Up via M-Pesa</Title>
                      <Text size="sm" c="dimmed">
                        Enter the M-Pesa number and amount. You'll receive a payment prompt on your phone.
                      </Text>
                    </Stack>

                    <form onSubmit={handleTopUp}>
                      <Stack gap="md">
                        <TextInput
                          label="M-Pesa Phone Number"
                          description="Safaricom number to charge"
                          placeholder="0712 345 678"
                          value={phone}
                          onChange={(e) => setPhone(e.currentTarget.value)}
                          size="md"
                          radius="md"
                          leftSection={<IconPhone size={16} color="#0077b6" />}
                          error={phoneError}
                          autoComplete="tel"
                          inputMode="tel"
                          styles={{ label: { fontWeight: 600 } }}
                        />

                        <NumberInput
                          label="Amount (KES)"
                          description={`Minimum KES ${MIN_AMOUNT}`}
                          placeholder="500"
                          value={amount}
                          onChange={setAmount}
                          size="md"
                          radius="md"
                          min={MIN_AMOUNT}
                          prefix="KES "
                          thousandSeparator=","
                          error={amountError}
                          styles={{ label: { fontWeight: 600 } }}
                        />

                        {stkError && (
                          <Alert color="red" radius="md" variant="light" icon={<IconAlertCircle size={16} />}>
                            {stkError}
                          </Alert>
                        )}

                        <Button
                          type="submit"
                          size="lg"
                          radius="md"
                          fullWidth
                          loading={stkLoading}
                          style={{ background: "linear-gradient(135deg, #03045e 0%, #0077b6 100%)" }}
                        >
                          Send Payment Request
                        </Button>
                      </Stack>
                    </form>
                  </Card>
                )}
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Stack>
    </PublicLayout>
  );
}

function DeviceInfoCard({
  info,
  loading,
  error,
}: {
  info: FengboDeviceInfoItem | null;
  loading: boolean;
  error: boolean;
}) {
  if (loading) {
    return (
      <Card shadow="xs" radius="lg" withBorder p="md">
        <Group gap="sm">
          <Loader size="xs" color="#0077b6" type="dots" />
          <Text size="sm" c="dimmed">Fetching meter data...</Text>
        </Group>
      </Card>
    );
  }

  if (error || !info) return null;

  const readingUnits = (Number(info.totalFlow) / 1000).toFixed(3);
  const valveOpen = String(info.valveStatus).toLowerCase() === "open" || info.valveStatus === 1;
  const valveLabel = info.valveStatus == null ? "--" : valveOpen ? "Open" : "Closed";
  const valveColor = info.valveStatus == null ? "#94a3b8" : valveOpen ? "#059669" : "#dc2626";

  return (
    <Card shadow="xs" radius="lg" withBorder p="md"
      style={{ background: "linear-gradient(135deg, #f0f9ff 0%, #faf5ff 100%)" }}
    >
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">Meter Data</Text>
      <SimpleGrid cols={4} spacing="xs">
        <Box style={{ textAlign: "center" }}>
          <IconCircleCheck size={18} color={valveColor} />
          <Text size="xs" c="dimmed" mt={2}>Valve</Text>
          <Text fw={700} size="sm" c={valveColor}>{valveLabel}</Text>
        </Box>
        <Box style={{ textAlign: "center" }}>
          <IconDroplet size={18} color="#0077b6" />
          <Text size="xs" c="dimmed" mt={2}>Reading (m³)</Text>
          <Text fw={700} size="sm">{readingUnits}</Text>
        </Box>
        <Box style={{ textAlign: "center" }}>
          <IconDroplet size={18} color="#7c3aed" />
          <Text size="xs" c="dimmed" mt={2}>Remaining (L)</Text>
          <Text fw={700} size="sm">
            {info.remainWater != null ? Number(info.remainWater).toFixed(1) : "--"}
          </Text>
        </Box>
        <Box style={{ textAlign: "center" }}>
          <IconBattery2 size={18} color="#059669" />
          <Text size="xs" c="dimmed" mt={2}>Battery</Text>
          <Text fw={700} size="sm">{info.batteryVoltage}V</Text>
        </Box>
      </SimpleGrid>
      {info.datetime && (
        <Text size="xs" c="dimmed" mt="sm" style={{ textAlign: "right" }}>
          As of {formatDate(info.datetime)}
        </Text>
      )}
    </Card>
  );
}

function StkSentCard({ phone, onReset }: { phone: string; onReset: () => void }) {
  return (
    <Card shadow="sm" radius="lg" withBorder p="xl" style={{ textAlign: "center" }}>
      <ThemeIcon size={64} radius="xl" color="green" variant="light" style={{ margin: "0 auto 16px" }}>
        <IconPhone size={32} />
      </ThemeIcon>
      <Title order={3} c="#03045e" mb={8}>Check Your Phone</Title>
      <Text size="sm" c="dimmed" mb={4}>An M-Pesa payment request has been sent to</Text>
      <Text fw={700} size="lg" mb="md" ff="monospace">{phone}</Text>
      <Text size="xs" c="dimmed" mb="xl">
        Enter your M-Pesa PIN on your phone to complete the payment. This may take a few seconds to arrive.
      </Text>
      <Button variant="light" color="gray" radius="md" onClick={onReset}>
        Make Another Payment
      </Button>
    </Card>
  );
}


function PaymentCard({ tx }: { tx: TransactionType }) {
  const isSuccess = !tx.processingError;

  return (
    <Card shadow="xs" radius="lg" withBorder p={0} style={{ overflow: "hidden" }}>
      <Box
        style={{
          height: 4,
          background: isSuccess
            ? "linear-gradient(90deg, #059669, #34d399)"
            : "linear-gradient(90deg, #dc2626, #f87171)",
        }}
      />
      <Box p="md">
        <Group justify="space-between" align="center" mb="md">
          <Text size="xs" c="dimmed" fw={500}>{tx.transTime ? formatDate(tx.transTime) : "--"}</Text>
          <Badge
            color={isSuccess ? "green" : "red"}
            variant="light"
            radius="xl"
            leftSection={isSuccess ? <IconCheck size={11} /> : <IconX size={11} />}
          >
            {isSuccess ? "Success" : "Failed"}
          </Badge>
        </Group>

        <Group justify="space-between" align="flex-end" mb="sm">
          <Box>
            <Text size="xs" c="dimmed" mb={2}>Amount Paid</Text>
            <Text fw={800} size="xl" c="#03045e">
              KES {formatNumberWithCommas(Number(tx.transAmount))}
            </Text>
          </Box>
          <Box style={{ textAlign: "right" }}>
            <Text size="xs" c="dimmed" mb={2}>Units Credited</Text>
            <Group gap={4} justify="flex-end">
              <IconDroplet size={14} color="#0077b6" />
              <Text fw={700} size="md" c="#0077b6">{Number(tx.litersCredited).toFixed(2)} units</Text>
            </Group>
          </Box>
        </Group>

        <Box style={{ height: 1, background: "#f1f5f9", margin: "12px 0" }} />

        <Group justify="space-between" wrap="wrap" gap="xs">
          {tx.firstName && (
            <Box>
              <Text size="xs" c="dimmed">Payer</Text>
              <Text size="sm" fw={600}>{tx.firstName}</Text>
            </Box>
          )}
          {tx.transId && (
            <Box style={{ textAlign: "right" }}>
              <Text size="xs" c="dimmed">M-Pesa Ref</Text>
              <Text size="sm" fw={600} ff="monospace" c="#475569">{tx.transId}</Text>
            </Box>
          )}
        </Group>

        {tx.processingError && (
          <Alert color="red" variant="light" radius="md" p="xs" mt="sm">
            <Text size="xs">{humanizeError(tx.processingError)}</Text>
          </Alert>
        )}
      </Box>
    </Card>
  );
}
